import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Upload, FileText, X, Tag } from 'lucide-react';
import { sapi } from '../../lib/api';
import { Spinner, useToast, Toast, ConfirmModal } from '../../components/UI';

interface CV { id: string; name: string; category: string; fileName: string; createdAt: string; }

const CATS = ['Software Development','Data Science','Design','Marketing','Finance','Engineering','Other'];

export default function CVsPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [cvs, setCvs] = useState<CV[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CV | null>(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', category: CATS[0] });
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    sapi.getCVs().then(setCvs).catch(() => setCvs([])).finally(() => setLoading(false));
  }, []);

  const upload = async () => {
    if (!form.name.trim()) { show('Name required', 'error'); return; }
    if (!file) { show('Please select a file', 'error'); return; }
    setUploading(true);
    try {
      const created = await sapi.uploadCV(form.name, form.category, file);
      setCvs(p => [created, ...p]);
      show(`"${form.name}" uploaded`, 'success');
      setModal(false); setForm({ name: '', category: CATS[0] }); setFile(null);
    } catch (e: any) { show(e.message, 'error'); }
    finally { setUploading(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    await sapi.deleteCV(deleteTarget.id).catch(() => null);
    setCvs(p => p.filter(c => c.id !== deleteTarget.id));
    show('CV deleted', 'info'); setDeleteTarget(null);
  };

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <div className="page-header">
        <div><h1 className="page-title">CVs</h1><p className="page-sub">{cvs.length} CV{cvs.length !== 1 ? 's' : ''} uploaded</p></div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={15} /> Upload CV</button>
      </div>

      {loading ? <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><Spinner size={28} /></div>
      : cvs.length === 0 ? (
        <div className="card"><div className="empty-state"><FileText size={32} /><h3>No CVs yet</h3><p>Upload your first CV to get started.</p><button className="btn btn-primary btn-sm" onClick={() => setModal(true)}><Plus size={13} /> Upload CV</button></div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {cvs.map(cv => (
            <div key={cv.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(108,99,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', flexShrink: 0 }}><FileText size={18} /></div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{cv.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{cv.fileName}</div>
                  </div>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(cv)}><Trash2 size={12} /></button>
              </div>
              {cv.category && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--accent)', background: 'rgba(108,99,255,0.08)', borderRadius: 6, padding: '4px 10px', width: 'fit-content' }}>
                  <Tag size={11} />{cv.category}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date(cv.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header"><h3>Upload CV</h3><button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={14} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label>CV Name *</label><input placeholder="e.g. Software Dev CV 2024" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus /></div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>File (PDF) *</label>
                <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s', background: file ? 'rgba(108,99,255,0.04)' : 'transparent' }}>
                  {file ? <><FileText size={20} color="var(--accent)" style={{ margin: '0 auto 6px' }} /><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{file.name}</div><div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{(file.size / 1024).toFixed(0)} KB</div></>
                  : <><Upload size={20} color="var(--text3)" style={{ margin: '0 auto 6px' }} /><div style={{ fontSize: 13, color: 'var(--text2)' }}>Click to choose PDF</div><div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>PDF, max 10MB</div></>}
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={e => setFile(e.target.files?.[0] || null)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={upload} disabled={uploading}>{uploading ? <Spinner size={14} /> : <Upload size={14} />} Upload</button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && <ConfirmModal title="Delete CV" message={<>Delete <strong>{deleteTarget.name}</strong>?</>} onConfirm={del} onCancel={() => setDeleteTarget(null)} danger />}
    </div>
  );
}
